// src/components/invest/FlexiOptInToggle.js
import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { get, post } from '../../libs/api';
import { isFlexiTenant } from '../../libs/tenant';
import ConfirmDialog from '../common/Modal';
import { Errors } from '../common/msg';
import { useCapabilityBlocked } from '../common/restriction';
import {
  CAPABILITY,
  RESTRICTED_MESSAGE,
  isRestrictedResponse,
} from '../../libs/restrictions';
import './FlexiOptInToggle.css';

const MODAL_ID = 'flexiConfirmModal';
const TOOLTIP_ID = 'flexi-optin-tip';

const showModal = () => window.$ && window.$(`#${MODAL_ID}`).modal('show');
const hideModal = () => window.$ && window.$(`#${MODAL_ID}`).modal('hide');

// The API body is {msg, code, data:{isFlexiEnabled}} with NO top-level `status`.
// `isFlexiEnabled` may come back as the string "true"/"false" or a boolean.
const readFlag = (flexi) => flexi?.data?.isFlexiEnabled;
const isTruthy = (v) => v === true || v === 'true';

const FlexiOptInToggle = () => {
  // Auto Flexi is PST-only (PB-1837). Captured once per mount (lazy initializer)
  // so the early return below can never change hook order across renders.
  const [eligible] = useState(isFlexiTenant);
  const [clientId, setClientId] = useState(null);
  const [enabled, setEnabled] = useState(null); // null = loading/unknown
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false); // value awaiting confirmation
  const [error, setError] = useState(null);
  // PB-2322 / Compliance Q5: under a block the client cannot turn Flexi on. Turning it off, and a
  // client already opted in, are unaffected.
  const optInBlocked = useCapabilityBlocked(CAPABILITY.ROLLOVER);

  useEffect(() => {
    if (!eligible) return undefined; // ineligible tenants issue no API calls
    let active = true;
    (async () => {
      try {
        const u = await post('/user/get');
        const id = u?.data?.manage_user?.clientId;
        if (!id) {
          if (active) setError('Could not load your Auto Flexi setting.');
          return;
        }
        if (active) setClientId(id);
        const flexi = await get(`/client/isFlexiEnabled?clientId=${id}`);
        const val = readFlag(flexi);
        if (active && val !== undefined && val !== null) {
          setEnabled(isTruthy(val));
        } else if (active) {
          setError('Could not load your Auto Flexi setting.');
        }
      } catch (e) {
        if (active) setError('Could not load your Auto Flexi setting.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eligible]);

  const requestChange = () => {
    if (loading || saving || enabled === null) return; // ignore until true state known
    setError(null);
    setPending(!enabled);
    showModal();
  };

  const proceed = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const flexi = await get(
        `/client/isFlexiEnabled?clientId=${clientId}&isFlexiEnabled=${pending}`
      );
      if (isRestrictedResponse(flexi)) {
        setError(RESTRICTED_MESSAGE);
        return;
      }
      const val = readFlag(flexi);
      if (val !== undefined && val !== null) {
        setEnabled(isTruthy(val));
      } else {
        setError('Could not update your Auto Flexi setting. Please try again.');
      }
    } catch (e) {
      setError('Could not update your Auto Flexi setting. Please try again.');
    } finally {
      setSaving(false);
      hideModal();
    }
  };

  const cancel = () => hideModal();

  // After all hooks: ineligible tenants render nothing, wherever this mounts.
  if (!eligible) return null;

  const on = enabled === true;
  const lockedOff = !on && optInBlocked;

  return (
    <div className="flexi-optin">
      <div className="d-flex align-items-center">
        <span style={{ marginRight: 4 }}>Auto Flexi Opt-in</span>
        <em
          className="icon ni ni-info text-soft nk-tooltip"
          data-tooltip-id={TOOLTIP_ID}
          style={{ cursor: 'help' }}
        ></em>
        <Tooltip
          id={TOOLTIP_ID}
          place="bottom"
          style={{ backgroundColor: '#101924', color: '#FFF', maxWidth: 280 }}
        >
          When ON, your matured Penguin Secure / SecureLend funds automatically
          roll into the 1-day Flexi product instead of returning to your wallet.
        </Tooltip>

        <div
          className="custom-control custom-switch flexi-switch"
          style={{ marginLeft: 12 }}
        >
          <input
            type="checkbox"
            role="switch"
            className="custom-control-input"
            id="flexiSwitch"
            checked={on}
            disabled={loading || saving || enabled === null || lockedOff}
            data-testid="flexi-optin-switch"
            onChange={requestChange}
          />
          <label
            className="custom-control-label"
            htmlFor="flexiSwitch"
            style={{ color: on ? '#1ee0ac' : '#8094ae', fontWeight: 600 }}
          >
            {loading
              ? '…'
              : saving
                ? 'Saving…'
                : enabled === null
                  ? '—'
                  : on
                    ? 'On'
                    : 'Off'}
          </label>
        </div>
      </div>

      {lockedOff && !error && (
        <p
          className="text-soft"
          data-testid="flexi-optin-restricted"
          style={{ fontSize: 12 }}
        >
          {RESTRICTED_MESSAGE}
        </p>
      )}
      {error && <Errors errors={[error]} />}

      <ConfirmDialog
        id={MODAL_ID}
        title={`You have chosen to ${pending ? 'enable' : 'disable'} Auto Flexi Opt-in. An email and Slack notification will be sent. Do you want to proceed?`}
        onCancel={cancel}
        onProceed={proceed}
      />
    </div>
  );
};

export default FlexiOptInToggle;
